import axios, { AxiosHeaders } from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance } from "index";

export const axiosInstance = createAuthenticatedAxiosInstance(
  process.env.REACT_APP_API_HOST!,
  process.env.REACT_APP_API_SCOPE!
);

export const jobApiClient = createAuthenticatedAxiosInstance(
  process.env.REACT_APP_JOBAPI_HOST!,
  process.env.REACT_APP_API_SCOPE! // Samma scope som tidigare enligt dig
);

function createAuthenticatedAxiosInstance(baseURL: string, scope: string) {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  instance.interceptors.request.use(
    async (config) => {
      const account = msalInstance.getActiveAccount();

      if (!account) {
        console.error("No active account! Unable to acquire access token.");
        return config;
      }

      const accessTokenRequest = {
        scopes: [scope],
        account,
      };

      try {
        const response = await msalInstance.acquireTokenSilent(accessTokenRequest);
        const token = response.accessToken;

        if (token) {
          if (!config.headers) {
            config.headers = new AxiosHeaders();
          }
          (config.headers as AxiosHeaders).set("Authorization", `Bearer ${token}`);
        }
      } catch (error: any) {
        const message = error?.message as string | undefined;
        const errorCode = error?.errorCode as string | undefined;

        if (
          error instanceof InteractionRequiredAuthError ||
          errorCode === "invalid_request" ||
          message?.includes("AADSTS90009")
        ) {
          console.error("Auth required to acquire token. Redirecting to login.");
          await msalInstance.acquireTokenRedirect(accessTokenRequest);
          return Promise.reject(error);
        }

        console.error("Token acquisition failed:", error);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      console.error("API call error:", error);
      
      // Handle 401 Unauthorized - token may have expired
      if (error.response?.status === 401) {
        const account = msalInstance.getActiveAccount();
        if (account) {
          try {
            // Try to re-authenticate
            await msalInstance.acquireTokenRedirect({
              scopes: [scope],
              account,
            });
          } catch (authError) {
            console.error("Re-authentication failed:", authError);
          }
        }
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
}
